import { type ClipboardEvent, useId, useState } from "react";
import { planEmailAdd } from "../../lib/emails.ts";
import styles from "../file/FilePage.module.css";
import ui from "../ui.module.css";

type Props = {
  inputId: string;
  existing: string[];
  owner: string | undefined;
  placeholder: string;
  failure: string | null;
  onAdd: (emails: string[]) => void;
  /** Handles text that names something other than addresses; returns true when it did. */
  onText?: (text: string) => boolean;
};

export function EmailAddForm({
  inputId,
  existing,
  owner,
  placeholder,
  failure,
  onAdd,
  onText,
}: Props) {
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const problemId = useId();

  const add = (text: string) => {
    if (onText?.(text.trim())) {
      setDraft("");
      setProblem(null);
      return;
    }
    const plan = planEmailAdd(text, existing, owner);
    if (plan.fresh.length > 0) onAdd(plan.fresh);
    setDraft(plan.leftover);
    setProblem(plan.problem);
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    if (!/[\s,;]/.test(text.trim())) return;
    event.preventDefault();
    add(`${draft} ${text}`);
  };

  return (
    <>
      <form
        className={styles.addForm}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          add(draft);
        }}
      >
        <input
          id={inputId}
          className={ui.input}
          type="email"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          placeholder={placeholder}
          value={draft}
          aria-invalid={problem && draft ? true : undefined}
          aria-describedby={problem ? problemId : undefined}
          onChange={(event) => {
            setDraft(event.currentTarget.value);
            if (problem) setProblem(null);
          }}
          onPaste={onPaste}
        />
        <button type="submit" className={ui.button} data-variant="primary">
          Add
        </button>
      </form>
      {problem || failure ? (
        <p id={problemId} className={styles.fieldError} role="alert">
          {problem ?? failure}
        </p>
      ) : null}
    </>
  );
}
